import React, { useEffect, useState, useRef } from 'react';
import Plot from 'react-plotly.js';
import axios from 'axios';
import CategoryIcon from '../CategoryIcon';

const API_URL = 'https://iracing6-backend.herokuapp.com';

function IRatingDistributionWidget({ config, onConfigChange }) {
  const [iRatings, setIRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dataType, setDataType] = useState(config.category || 'SportsCar');
  const [timeFrame, setTimeFrame] = useState(config.timeFrame || 'All Time');
  const [startRange, setStartRange] = useState(config.startRange || 'All');
  const [licenseClass, setLicenseClass] = useState(config.licenseClass || 'All');
  const isInitialMount = useRef(true);

  const getTimeFrameValue = (tf) => {
    switch (tf) {
      case '1 Year': return 2024;
      case '2 Years': return 2023;
      case '3 Years': return 2022;
      case '4 Years': return 2021;
      case 'All Time':
      default: return 1;
    }
  };

  const getStartRangeValue = (range) => {
    switch (range) {
      case '5-50': return '5-50';
      case '51-200': return '51-200';
      case '201-500': return '201-500';
      case '501+': return '501+';
      case 'All':
      default: return null;
    }
  };

  const getLicenseClassValue = (lc) => {
    switch (lc) {
      case 'Rookie': return 'rookie';
      case 'Class D': return 'class_d';
      case 'Class C': return 'class_c';
      case 'Class B': return 'class_b';
      case 'Class A': return 'class_a';
      case 'Pro': return 'pro';
      case 'All':
      default: return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const timeFrameValue = getTimeFrameValue(timeFrame);
        const rangeValue = getStartRangeValue(startRange);
        const licenseValue = getLicenseClassValue(licenseClass);

        let url;
        if (licenseValue) {
          if (rangeValue) {
            if (timeFrameValue === 1) {
              url = `${API_URL}/api/irating-data/iratings${dataType}ByLicenseAndStarts/${licenseValue}/${rangeValue}`;
            } else {
              url = `${API_URL}/api/irating-data/iratings${dataType}ByLicense/${licenseValue}/${timeFrameValue}/${rangeValue}`;
            }
          } else {
            if (timeFrameValue === 1) {
              url = `${API_URL}/api/irating-data/iratings${dataType}ByLicense/${licenseValue}`;
            } else {
              url = `${API_URL}/api/irating-data/iratings${dataType}ByLicense/${licenseValue}/${timeFrameValue}`;
            }
          }
        } else {
          url = `${API_URL}/api/irating-data/iratings${dataType}/${timeFrameValue}`;
          if (rangeValue) {
            url += `/${rangeValue}`;
          }
        }

        const response = await axios.get(url);
        setIRatings(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [dataType, timeFrame, startRange, licenseClass]);

  // Persist filter state to backend config
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (onConfigChange) {
      onConfigChange({
        ...config,
        category: dataType,
        timeFrame,
        startRange,
        licenseClass,
      });
    }
  }, [dataType, timeFrame, startRange, licenseClass]);

  if (loading) {
    return <div className="widget-loading"><div className="spinner-small"></div></div>;
  }

  if (error) {
    return <div className="widget-error">Error: {error}</div>;
  }

  if (!iRatings || iRatings.length === 0) {
    return <div className="widget-error">No data available</div>;
  }

  // Group iRatings by 100-point ranges
  const groupedIRatings = {};
  iRatings.forEach((iRating) => {
    const range = Math.floor(iRating / 100) * 100;
    groupedIRatings[range] = (groupedIRatings[range] || 0) + 1;
  });

  const maxRange = Math.max(...Object.keys(groupedIRatings).map(Number));
  const minRange = Math.min(...Object.keys(groupedIRatings).map(Number));

  // Calculate stats
  const mean = iRatings.reduce((sum, r) => sum + r, 0) / iRatings.length;
  const sortedRatings = [...iRatings].sort((a, b) => a - b);
  const median = sortedRatings.length % 2 === 0
    ? (sortedRatings[sortedRatings.length / 2 - 1] + sortedRatings[sortedRatings.length / 2]) / 2
    : sortedRatings[Math.floor(sortedRatings.length / 2)];

  const plotData = [{
    x: Object.keys(groupedIRatings).map(r => parseInt(r, 10) + 50),
    y: Object.values(groupedIRatings),
    type: 'bar',
    marker: {
      color: '#154fa7',
      line: {
        color: '#3e8bff',
        width: 1,
      },
    },
    hovertemplate: '%{x}: %{y}<extra></extra>',
  }];

  const layout = {
    margin: { l: 35, r: 10, t: 10, b: 25 },
    plot_bgcolor: 'transparent',
    paper_bgcolor: 'transparent',
    xaxis: {
      title: '',
      color: '#aaa',
      gridcolor: '#333',
      range: licenseClass === 'Pro'
        ? [Math.max(0, minRange - 1000), maxRange + 1000]
        : [0, 5000],
      dtick: 1000,
      fixedrange: true,
    },
    yaxis: {
      title: '',
      color: '#aaa',
      gridcolor: '#333',
      fixedrange: true,
      dtick: licenseClass === 'Pro' ? 1 : undefined,
    },
    font: {
      family: 'Questrial, sans-serif',
      size: 10,
      color: '#ddd',
    },
    showlegend: false,
    autosize: true,
  };

  const categories = [
    { key: 'SportsCar', cat: 'sports_car' },
    { key: 'FormulaCar', cat: 'formula_car' },
    { key: 'Oval', cat: 'oval' },
    { key: 'DirtRoad', cat: 'dirt_road' },
    { key: 'DirtOval', cat: 'dirt_oval' },
  ];

  const timeFrames = ['All Time', '1 Year', '2 Years', '3 Years', '4 Years'];
  const startRanges = ['All', '5-50', '51-200', '201-500', '501+'];
  const licenseClasses = ['All', 'Rookie', 'Class D', 'Class C', 'Class B', 'Class A', 'Pro'];

  const licenseLabel = (lc) => {
    if (lc === 'All') return 'All';
    if (lc === 'Rookie') return 'R';
    if (lc === 'Pro') return 'Pro';
    return lc.replace('Class ', '');
  };

  const licenseColor = (lc) => {
    switch (lc) {
      case 'Class A': return '#3498db';
      case 'Class B': return '#2ecc71';
      case 'Class C': return '#f1c40f';
      case 'Class D': return '#e67e22';
      case 'Rookie': return '#e74c3c';
      default: return undefined;
    }
  };

  return (
    <div className="ir-widget">
      <div className="irw-filters">
        <div className="irw-filter-row">
          <div className="irw-btn-group">
            {categories.map(({ key, cat }) => (
              <button
                key={key}
                className={`spw-toggle-btn ${dataType === key ? 'spw-toggle-active' : ''}`}
                onClick={() => { setDataType(key); setLicenseClass('All'); }}
                title={cat.replace(/_/g, ' ')}
              >
                <CategoryIcon category={cat} size={14} />
              </button>
            ))}
          </div>
          <div className="spw-separator" />
          <div className="irw-btn-group">
            {timeFrames.map((tf) => (
              <button
                key={tf}
                className={`irw-text-btn ${timeFrame === tf ? 'spw-toggle-active' : ''}`}
                onClick={() => setTimeFrame(tf)}
              >
                {tf === 'All Time' ? 'All' : tf.replace(' Years', 'y').replace(' Year', 'y')}
              </button>
            ))}
          </div>
        </div>
        <div className="irw-filter-row">
          <div className="irw-btn-group">
            <span className="spw-filter-label">Races:</span>
            {startRanges.map((sr) => (
              <button
                key={sr}
                className={`irw-text-btn ${startRange === sr ? 'spw-toggle-active' : ''}`}
                onClick={() => setStartRange(sr)}
              >
                {sr === 'All' ? '1+' : sr}
              </button>
            ))}
          </div>
          <div className="spw-separator" />
          <div className="irw-btn-group">
            <span className="spw-filter-label">Lic:</span>
            {licenseClasses.map((lc) => (
              <button
                key={lc}
                className={`irw-text-btn ${licenseClass === lc ? 'spw-toggle-active' : ''}`}
                onClick={() => setLicenseClass(lc)}
                style={licenseColor(lc) ? { color: licenseColor(lc) } : undefined}
              >
                {licenseLabel(lc)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="ir-widget-chart">
        <Plot
          data={plotData}
          layout={layout}
          config={{ displayModeBar: false, responsive: true }}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
        />
      </div>
      <div className="ir-widget-stats">
        <span>Mean: <strong>{mean.toFixed(0)}</strong></span>
        <span>Median: <strong>{median.toFixed(0)}</strong></span>
      </div>
    </div>
  );
}

export default IRatingDistributionWidget;
